import { useQuery } from '@tanstack/react-query';

import type { GetMarkInspectedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get inspection data from a purchased vehicle
 */
export const getMarkInspected = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkInspectedResponse> => {
	const response = await apiClient.get<GetMarkInspectedResponse>(
		ordersEndpoints.markInspected(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting inspection data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkInspected = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx.markInspected.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get inspection data without an order ID.');
			}
			return getMarkInspected(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
